import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { FaEllipsisH } from 'react-icons/fa';

/**
 * ActionDropdown — an action-menu kebab/trigger (react-bootstrap `Dropdown`
 * equivalent for row actions and header menus).
 *
 * Unlike TwDropdown (a value-select), items fire `onClick` actions.
 * The menu renders in a portal above modals (zIndex 100001) with the same
 * outside-click / Escape / scroll-reposition behavior as TwDropdown.
 */
const ActionDropdown = ({
  trigger,
  triggerClassName = '',
  // When true the default square-icon-button styling is dropped and
  // triggerClassName fully controls the trigger's appearance (text triggers).
  unstyledTrigger = false,
  align = 'end',
  menuWidth = 190,
  disabled = false,
  items = [],
}) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const width = parseInt(menuWidth, 10) || 190;
    let left = align === 'end' ? r.right - width : r.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    if (left < 8) left = 8;
    setPos({ top: r.bottom + 4, left });
  }, [menuWidth, align]);

  useLayoutEffect(() => { if (open) updatePos(); }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => updatePos();
    const onResize = () => updatePos();
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
          && triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updatePos]);

  const handleItemClick = (item) => {
    setOpen(false);
    item.onClick?.();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        className={unstyledTrigger
          ? triggerClassName
          : `tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed ${triggerClassName}`}
      >
        {trigger || <FaEllipsisH size={13} />}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: parseInt(menuWidth, 10) || 190,
            maxHeight: '320px',
            zIndex: 100001,
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 10px 30px rgba(15,23,42,0.12), 0 2px 6px rgba(15,23,42,0.06)',
            overflow: 'auto',
            padding: '4px 0',
          }}
        >
          {items.map((item, index) => {
            if (item.divider) {
              return <div key={`divider-${index}`} className="tw-my-1 tw-border-t tw-border-solid tw-border-slate-100" />;
            }

            return (
              <button
                // A node label (e.g. label + hint line) would stringify to "[object Object]".
                key={item.key ?? (typeof item.label === 'string' ? item.label : index)}
                type="button"
                disabled={item.disabled}
                onClick={() => handleItemClick(item)}
                className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed ${
                  item.danger
                    ? 'tw-text-red-600 hover:tw-bg-red-50'
                    : 'tw-text-slate-600 hover:tw-bg-slate-50'
                }`}
              >
                {item.icon && <span className="tw-flex-shrink-0 tw-inline-flex">{item.icon}</span>}
                <span className="tw-flex-1 tw-truncate">{item.label}</span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

ActionDropdown.propTypes = {
  trigger: PropTypes.node,
  triggerClassName: PropTypes.string,
  unstyledTrigger: PropTypes.bool,
  align: PropTypes.oneOf(['start', 'end']),
  menuWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.node,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    danger: PropTypes.bool,
    divider: PropTypes.bool,
  })).isRequired,
};

export default ActionDropdown;
